/**
 * Walmart Retail Link bot connection status.
 *
 * Mirrors `WalmartBotStatus` in `server/src/integration/walmart/portal.rs`. The token itself is
 * never sent to the client — only whether one is saved and how much of its 60 days remain.
 */

export type BotTokenStatus =
  | { status: "unknown" }
  | { status: "active"; days_remaining: number }
  | { status: "expiring_soon"; days_remaining: number }
  | { status: "expired"; days_ago: number }

export interface WalmartBotStatus {
  bot_token_saved: boolean
  token_status: BotTokenStatus
  bot_token_expires_at: string | null
  bot_token_generated_at: string | null
  vendor_number: string | null
  portal_sync_enabled: boolean
  download_endpoint_configured: boolean
  last_login_error: string | null
  last_synced_at: string | null
}

export interface BotTokenMessage {
  text: string
  severity: "ok" | "warn" | "error"
}

/**
 * What to tell the brand about their bot token. A missing token and an expired one both mean the
 * sync can't log in, so both read as errors; a token inside the warning window still works but needs
 * rotating before it stops.
 */
export function bot_token_message(status: WalmartBotStatus): BotTokenMessage {
  if (!status.bot_token_saved) {
    return { text: "No bot token saved — Walmart sync can't sign in", severity: "error" }
  }
  switch (status.token_status.status) {
    case "expired": {
      const days = status.token_status.days_ago
      return {
        text: days === 0 ? "Bot token expired today" : `Bot token expired ${days} days ago`,
        severity: "error",
      }
    }
    case "expiring_soon": {
      const days = status.token_status.days_remaining
      return {
        text: `Bot token expires in ${days} ${days === 1 ? "day" : "days"}`,
        severity: "warn",
      }
    }
    case "active":
      return { text: `Bot token valid for ${status.token_status.days_remaining} more days`, severity: "ok" }
    case "unknown":
      return { text: "Bot token saved (expiry date unknown)", severity: "warn" }
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function status(overrides: Partial<WalmartBotStatus> = {}): WalmartBotStatus {
    return {
      bot_token_saved: true,
      token_status: { status: "active", days_remaining: 45 },
      bot_token_expires_at: null,
      bot_token_generated_at: null,
      vendor_number: "123456",
      portal_sync_enabled: true,
      download_endpoint_configured: true,
      last_login_error: null,
      last_synced_at: null,
      ...overrides,
    }
  }

  describe("bot_token_message", () => {
    it("treats a missing token as blocking, whatever the dates say", () => {
      const message = bot_token_message(status({ bot_token_saved: false }))
      expect(message.severity).toBe("error")
      expect(message.text).toContain("No bot token")
    })

    it("warns before expiry and errors after", () => {
      expect(bot_token_message(status({ token_status: { status: "expiring_soon", days_remaining: 5 } }))).toEqual({
        text: "Bot token expires in 5 days",
        severity: "warn",
      })
      expect(bot_token_message(status({ token_status: { status: "expired", days_ago: 2 } }))).toEqual({
        text: "Bot token expired 2 days ago",
        severity: "error",
      })
    })

    it("uses singular days for a token expiring tomorrow", () => {
      expect(bot_token_message(status({ token_status: { status: "expiring_soon", days_remaining: 1 } })).text).toBe(
        "Bot token expires in 1 day"
      )
    })

    it("does not claim a healthy token is a problem", () => {
      expect(bot_token_message(status()).severity).toBe("ok")
    })

    it("flags an unrecorded expiry as worth attention without calling it expired", () => {
      const message = bot_token_message(status({ token_status: { status: "unknown" } }))
      expect(message.severity).toBe("warn")
      expect(message.text).not.toContain("expired")
    })
  })
}
